import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Pencil, Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import {
    CATEGORY_COLOR_KEYS,
    Category,
    CategoryUsage,
    getCategoryColorClasses,
} from './categoryColors';
import { CategoryChip } from './CategoryChip';
import CategoryForm from './CategoryForm';

interface CategoryPickerProps {
    value: Category | null;
    onChange: (next: Category | null) => void;
    existing: CategoryUsage[];
    mode: 'create' | 'edit';
    onRename?: (rename: { from: Category; to: Category }) => void;
    className?: string;
}

export const CategoryPicker = ({
    value,
    onChange,
    existing,
    mode,
    onRename,
    className,
}: CategoryPickerProps) => {
    const { t } = useTranslation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverView, setPopoverView] = useState<'list' | 'new'>('list');
    const [inlineEditing, setInlineEditing] = useState(false);

    useEffect(() => {
        if (!popoverOpen) setPopoverView('list');
    }, [popoverOpen]);

    const allTaken = useMemo(
        () => new Set(existing.map((c) => c.color)),
        [existing]
    );

    const takenForEdit = useMemo(() => {
        if (!value) return allTaken;
        const next = new Set(allTaken);
        next.delete(value.color);
        return next;
    }, [allTaken, value]);

    if (inlineEditing && value) {
        return (
            <CategoryForm
                initialName={value.name}
                initialColor={value.color}
                taken={takenForEdit}
                onSave={(next) => {
                    if (
                        next.name !== value.name ||
                        next.color !== value.color
                    ) {
                        onRename?.({ from: value, to: next });
                        onChange(next);
                    }
                    setInlineEditing(false);
                }}
                onCancel={() => setInlineEditing(false)}
            />
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'flex h-9 w-full max-w-xs items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring',
                            className
                        )}
                    >
                        {value ? (
                            <CategoryChip category={value} />
                        ) : (
                            <span className="text-muted-foreground">
                                {t('folders.category.none')}
                            </span>
                        )}
                        <ChevronDown
                            className="h-4 w-4 shrink-0 opacity-50"
                            aria-hidden="true"
                        />
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-2">
                    {popoverView === 'list' ? (
                        <div className="flex flex-col gap-1">
                            {existing.length > 0 && (
                                <ul className="flex flex-col gap-0.5">
                                    {existing.map((category) => {
                                        const isSelected =
                                            value?.name === category.name &&
                                            value?.color === category.color;
                                        return (
                                            <li
                                                key={`${category.name}-${category.color}`}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        onChange({
                                                            name: category.name,
                                                            color: category.color,
                                                        });
                                                        setPopoverOpen(false);
                                                    }}
                                                    className={cn(
                                                        'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                                                        isSelected && 'bg-muted'
                                                    )}
                                                >
                                                    <span className="flex min-w-0 items-center gap-2">
                                                        <span
                                                            className={cn(
                                                                'h-3 w-3 shrink-0 rounded-full',
                                                                getCategoryColorClasses(
                                                                    category.color
                                                                ).swatch
                                                            )}
                                                        />
                                                        <span className="truncate text-foreground">
                                                            {category.name}
                                                        </span>
                                                    </span>
                                                    <span className="shrink-0 text-subtle-foreground">
                                                        {category.count}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            <div className="my-1 h-px bg-subtle" />
                            <button
                                type="button"
                                onClick={() => {
                                    onChange(null);
                                    setPopoverOpen(false);
                                }}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-subtle-foreground transition-colors hover:bg-muted"
                            >
                                <X className="h-3 w-3" aria-hidden="true" />
                                {t('folders.category.none')}
                            </button>
                            <button
                                type="button"
                                disabled={
                                    allTaken.size >= CATEGORY_COLOR_KEYS.length
                                }
                                onClick={() => setPopoverView('new')}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:text-subtle-foreground"
                            >
                                <Plus className="h-3 w-3" aria-hidden="true" />
                                {allTaken.size >= CATEGORY_COLOR_KEYS.length
                                    ? t('folders.category.allColorsUsed')
                                    : t('folders.category.newCategory')}
                            </button>
                        </div>
                    ) : (
                        <CategoryForm
                            initialName=""
                            initialColor={null}
                            taken={allTaken}
                            onSave={(next) => {
                                onChange(next);
                                setPopoverOpen(false);
                            }}
                            onCancel={() => setPopoverView('list')}
                        />
                    )}
                </PopoverContent>
            </Popover>
            {mode === 'edit' && value && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setInlineEditing(true)}
                    aria-label={t('folders.category.editAria')}
                >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
            )}
        </div>
    );
};
